import React, { useEffect, useState } from 'react';
import Modal from '@/components/front-office/Modal';
import Cart from '@/components/front-office/orders/Cart';
import BottomDiv from '@/assets/media/SVGComponents/BottomDiv';
import MenuSVG from '@/assets/media/SVGComponents/MenuSVG';
import CartSVG from '@/assets/media/SVGComponents/CartSVG';
import MoreSVG from '@/assets/media/SVGComponents/MoreSVG';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrdersContext } from '@/contexts/OrderContext';
import useDeliveryFees from '@/hooks/useDeliveryFees';
import Button from '@/components/Button';
import { usePaymentConfirmedContext } from '@/contexts/PaymentConfirmedContext';
import axios from 'axios';
import { toast } from 'react-toastify';

const BottomNavbar = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [showCartModal, setShowCartModal] = useState(false);
	const [showChatModal, setShowChatModal] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const isInOrderContext = location.pathname.startsWith('/commande');
	const isPaymentPage = location.pathname === '/commande/paiement';
	let setPaymentConfirmedObject = null;
	let cartFromContext = null;
	let handleGetTotalPrice = null;
	let handleCalculateDeliveryFee = null;
	let promoApplied = null;
	let walletModalOpen = false;

	try {
		const { handleGetTotalPrice: getTotalPrice, promoApplied: promo, isWalletModalOpen: walletModal } = useOrdersContext();
		const { handleCalculateDeliveryFee: calculateDeliveryFee } = useDeliveryFees();
		handleGetTotalPrice = getTotalPrice;
		handleCalculateDeliveryFee = calculateDeliveryFee;
		promoApplied = promo;
		walletModalOpen = walletModal || false;

		if (isPaymentPage) {
			const { setPaymentConfirmedObject: setPaymentConfirmed } = usePaymentConfirmedContext();
			setPaymentConfirmedObject = setPaymentConfirmed;
		}

		if (isInOrderContext) {
			const { cart } = useOrdersContext();
			cartFromContext = cart;
		}
	} catch (error) {
		console.log('OrderContext non disponible');
	}

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const getTotalAmount = () => {
		if (!isInOrderContext || !cartFromContext || !handleGetTotalPrice || !handleCalculateDeliveryFee) return 0;
		const total = handleGetTotalPrice().total;
		const deliveryFee = handleCalculateDeliveryFee();
		const result = total + deliveryFee;

		// Check if the result is decimal or integer and format accordingly
		return result % 1 === 0 ? result : result.toFixed(2);
	};

	const hasProducts = cartFromContext?.categories.some(category => category.selected_products?.length > 0 || category.extra_products?.length > 0);

	const handlePayment = async (e) => {
		e.preventDefault();

		if (isSubmitting) return;
		if (!cartFromContext?.canSubmitPayment) {
			toast.error('Veuillez corriger les erreurs du formulaire (téléphone au format français 0X XX XX XX XX, email, etc.).', {
				position: 'top-center',
				autoClose: 4000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
			return;
		}
		setIsSubmitting(true);

		if (!isInOrderContext || !cartFromContext || !handleGetTotalPrice || !handleCalculateDeliveryFee) return;

		const cart = cartFromContext;

		// // Créer l'objet de données directement sans FormData
		const orderData = {
			restaurant_id: cart.restaurant_id,
			delivery: {
				customer: {
					firstname: cart.delivery_firstname,
					lastname: cart.delivery_lastname,
					phone: cart.delivery_phone,
					message: cart?.delivery_message,
				},
				address: cart.delivery_address,
				fees: promoApplied && promoApplied.discountType === 'free' ? 0 : handleCalculateDeliveryFee(),
				date: cart?.delivery_date,
				time: cart?.delivery_time,
			},
			billing: {
				customer: {
					firstname: cart.billing_firstname,
					lastname: cart.billing_lastname,
					email: cart.billing_email,
					phone: cart.billing_phone,
					message: cart?.billing_message,
					company: cart?.billing_company,
				},
				address: cart.billing_address,
			},
			selected_menus: cart.menus.map((menu) => ({
				id: menu.id,
				name: menu.name,
				quantity: menu.menu_quantity,
				price: menu.price,
				vat_id: menu?.vat_id,
			})),
			categories: cart.categories
				.filter((category) => cart.is_no_limit && cart.is_no_limit_type === 'category' ? category.id === cart.is_no_limit_selected_category.id : true)
				.map((category) => ({
					category_id: category.id,
					name: category.name,
					total_quantity:
						category.selected_products.reduce((acc, product) => acc + product.selected_quantity, 0) +
						category.extra_products.reduce((acc, product) => acc + product.selected_quantity, 0),
					products: category.selected_products.map((product) => ({
						id: product.id,
						product_id: product.product_id,
						name: product.name,
						quantity: product.selected_quantity,
						price: product.price,
						vat_id: product.vat_id,
						weight: product.weight
					})),
					extra_products: category.extra_products.map((extraProduct) => ({
						id: extraProduct.id,
						product_id: extraProduct.product_id,
						name: extraProduct.name,
						quantity: extraProduct.selected_quantity,
						price: extraProduct.price,
						vat_id: extraProduct.vat_id,
						weight: extraProduct.weight
					})),
				})),
			promo_code: cart?.promo_code ?? null,
			total_price: handleGetTotalPrice().total + handleCalculateDeliveryFee(),
			status: promoApplied && promoApplied.discountType === 'free' ? 'validated' : 'pending_payment',
			is_no_limit: cart.is_no_limit
		};

		const total = promoApplied && promoApplied.discountType === 'free'
			? 0
			: (handleGetTotalPrice().totalWithPromo >= 0 && promoApplied ? handleGetTotalPrice().totalWithPromo : handleGetTotalPrice().total) + handleCalculateDeliveryFee();

		try {
			// Envoyer les données en JSON
			const response = await axios.post(
				`${import.meta.env.VITE_API_URL}/api/orders/create`,
				orderData,
				{
					headers: {
						'Content-Type': 'application/json'
					}
				}
			);

			if (response.status === 200) {
				setPaymentConfirmedObject({ restaurantId: cart?.restaurant_id, orderId: response.data.orderId, orderReference: response.data.reference });

				// Pour les commandes gratuites, redirigez directement vers la page de confirmation
				if (promoApplied && promoApplied.discountType === 'free') {
					toast.success('Commande gratuite validée avec succès !', {
						position: 'top-center',
						autoClose: 1500,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: false,
						draggable: true,
						theme: 'colored',
					});
					// Nettoyer le panier
					localStorage.removeItem('restaurant_id_ref');
					sessionStorage.removeItem('selectedRestaurant');
					navigate(`/commande/confirmation`, { replace: true });
				} else {
					// Pour les commandes normales, redirigez vers le formulaire de paiement
					navigate(`/commande/payment-form?amount=${total}&customerEmail=${cart.billing_email}`);
				}
			}
		} catch (error) {
			const { errors } = error.response.data;

			errors.forEach((error) => {
				toast.error(error.msg, {
					position: 'top-center',
					autoClose: 1200,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			});
			// Réactiver le bouton après une erreur
			setIsSubmitting(false);
		}
	};

	return (
		<>
			{showCartModal && isInOrderContext && (
				<Modal setShowModal={setShowCartModal} showFooterButtons={false} withHeader={false}>
					<div className='relative'>
						<div className="z-50 absolute top-2 right-2 cursor-pointer" onClick={() => setShowCartModal(false)}>
							<i className="fa-solid fa-xmark bg-primary text-white text-2xl font-bold w-8 h-8 border border-primary rounded-full flex items-center justify-center"></i>
						</div>
						<Cart showNextButton={false} withNavigation={false} />
					</div>
				</Modal>
			)}

			{showChatModal && (
				<Modal setShowModal={setShowChatModal} showFooterButtons={false} withHeader={false}>
					<div className='relative'>
						<div className="z-50 absolute top-2 right-2 cursor-pointer" onClick={() => setShowChatModal(false)}>
							<i className="fa-solid fa-xmark bg-primary text-white text-2xl font-bold w-8 h-8 border border-primary rounded-full flex items-center justify-center"></i>
						</div>
						<iframe
							src="https://www.chatbase.co/chatbot-iframe/0J2THFo83WMy7EXYBFatq"
							width="100%"
							style={{ height: '700px' }}
							frameBorder="0"
						/>
					</div>
				</Modal>
			)}

			{/* Bulle de chat desktop */}
			<div className="hidden lg:block fixed bottom-8 right-8 z-50">
				<div
					className="bg-primary text-white w-16 h-16 rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-primary-dark transition-colors"
					onClick={() => setShowChatModal(true)}
				>
					<i className="fa-solid fa-comments text-2xl"></i>
				</div>
			</div>

			{isInOrderContext && isPaymentPage && cartFromContext && !walletModalOpen ? (
				<div className="fixed bottom-0 w-full max-h-48 md:max-h-36 lg:hidden z-50">
					{/* <BottomDiv width="100%" height="70px" /> */}
					<Button
						text={promoApplied && promoApplied.discountType === 'free' ? 'Valider la commande' : 'Payer'}
						type={'Button'}
						action="button"
						withTopMargin={false}
						extraClasses="mb-4"
						disabled={cartFromContext.canSubmitPayment || isSubmitting}
						handleClick={handlePayment}
					/>
				</div>
			) : (
				<div className="fixed bottom-0 w-full max-h-48 md:max-h-36 lg:hidden">
					<BottomDiv width="100%" height="70px" />

					<div className="absolute bottom-0 w-full grid grid-cols-5 items-center text-center h-full">
						<a href="https://dev.rivkatraiteur.com" className="flex flex-col items-center justify-center text-gray-500 bg-white h-full">
							<MenuSVG width="20" height="20" color="currentColor" />
							<span className="text-sm">Menu</span>
						</a>
						<a href="/commande/adresse" className="flex flex-col items-center justify-center text-gray-500">
							<CartSVG width="20" height="20" color="currentColor" />
							<span className="text-sm">Commander</span>
						</a>

						<div className={`flex justify-center items-center -translate-y-1/2 md:-translate-y-2/4`} onClick={() => isInOrderContext ? setShowCartModal(true) : window.location.href = '/'}>
							<div className={`transition duration-300 ${isInOrderContext && hasProducts ? 'bg-primary' : 'bg-gray-400'} w-12 h-12 rounded-full flex items-center justify-center md:w-24 md:h-24`}>
								<div className="text-white text-sm md:text-2xl">
									{isInOrderContext && hasProducts ? `${getTotalAmount()}€` : <CartSVG width="24" height="24" color="white" />}
								</div>
							</div>
						</div>

						<div className="flex flex-col items-center justify-center text-gray-500" onClick={() => setShowChatModal(true)}>
							<i className="fa-solid fa-comments text-xl"></i>
							<span className="text-sm">Chat</span>
						</div>

						<div className="flex flex-col items-center justify-center text-gray-500 bg-white h-full" onClick={toggleMenu}>
							<MoreSVG width="20" height="20" color="currentColor" />
						</div>
					</div>
				</div>
			)}

			<div className={`fixed top-0 w-[300px] h-full bg-white transition-right duration-300 z-10 shadow-xl ${isMenuOpen ? 'right-0' : 'right-[-300px]'}`}>
				<button className="bg-primary rounded-full w-8 h-8 text-white text-lg cursor-pointer ml-2 mt-4" onClick={toggleMenu}>
					<i className="fa-solid fa-xmark"></i>
				</button>
				<div className="grid grid-cols-1 h-[calc(100vh-160px)] mt-4 justify-between">
					<ul className="list-none p-0 self-end">
						<li className='my-0 mx-4'><a href="/cgv">Conditions Générales de Vente</a></li>
						<li className='my-0 mx-4'><a href="/mentions-legales">Mentions Légales</a></li>
					</ul>
				</div>
			</div>
		</>
	);
};

export default BottomNavbar;
